import * as THREE from 'three';

/**
 * Represents an arrow shot by an enemy archer
 */
export class EnemyArrow {
  private object: THREE.Object3D;
  private arrowBody: THREE.Mesh;
  private arrowHead: THREE.Mesh;
  private scene: THREE.Scene;
  private position: THREE.Vector3;
  private direction: THREE.Vector3;
  private velocity: THREE.Vector3;
  private speed: number = 15.0;
  private gravity: number = 9.8;
  private damage: number;
  private active: boolean = true;
  private lifetime: number = 5.0; // Seconds before arrow despawns
  private currentLifetime: number = 0;
  private hasHit: boolean = false;
  
  /**
   * Creates a new enemy arrow
   * @param position The starting position
   * @param direction The direction to fire in
   * @param damage The damage the arrow deals
   * @param scene The scene to add the arrow to
   */
  constructor(position: THREE.Vector3, direction: THREE.Vector3, damage: number, scene: THREE.Scene) {
    this.position = position.clone();
    this.direction = direction.clone().normalize();
    this.damage = damage;
    this.scene = scene;
    
    // Calculate initial velocity
    this.velocity = this.direction.clone().multiplyScalar(this.speed);
    
    // Create arrow object
    this.object = new THREE.Object3D();
    this.object.position.copy(this.position);
    
    // Create arrow body
    const bodyGeometry = new THREE.CylinderGeometry(0.02, 0.02, 0.5, 8);
    const bodyMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    this.arrowBody = new THREE.Mesh(bodyGeometry, bodyMaterial);
    this.object.add(this.arrowBody);
    
    // Create arrow head
    const headGeometry = new THREE.ConeGeometry(0.04, 0.1, 8);
    const headMaterial = new THREE.MeshStandardMaterial({ color: 0x888888 });
    this.arrowHead = new THREE.Mesh(headGeometry, headMaterial);
    this.arrowHead.position.y = 0.3;
    this.object.add(this.arrowHead);
    
    // Rotate arrow to point in the direction of travel
    this.updateRotation();
    
    // Add to scene
    scene.add(this.object);
  }
  
  /**
   * Updates the arrow's rotation to match its direction
   */
  private updateRotation(): void {
    // Create a quaternion that rotates from the default arrow orientation (up) to the direction
    const upVector = new THREE.Vector3(0, 1, 0);
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(upVector, this.direction);
    
    // Apply the rotation
    this.object.quaternion.copy(quaternion);
  }
  
  /**
   * Updates the arrow
   * @param deltaTime Time since last update in seconds
   */
  public update(deltaTime: number): void {
    if (!this.active) return;
    
    // Update lifetime
    this.currentLifetime += deltaTime;
    if (this.currentLifetime >= this.lifetime) {
      this.deactivate();
      return;
    }
    
    // If the arrow has hit something, don't update position
    if (this.hasHit) return;
    
    // Apply gravity to velocity
    this.velocity.y -= this.gravity * deltaTime;
    
    // Update direction based on velocity
    this.direction.copy(this.velocity).normalize();
    
    // Update rotation to match direction
    this.updateRotation();
    
    // Update position
    const movement = this.velocity.clone().multiplyScalar(deltaTime);
    this.position.add(movement);
    this.object.position.copy(this.position);
    
    // Check for collisions
    this.checkCollisions();
  }
  
  /**
   * Checks for collisions with the environment and player
   */
  private checkCollisions(): void {
    // Check for ground collision
    if (this.position.y <= 0) {
      this.position.y = 0;
      this.object.position.copy(this.position);
      this.handleHit();
    }
    
    // In a real implementation, we would check for collisions with the player and other objects
    // For now, we'll just handle ground collisions
  }
  
  /**
   * Handles the arrow hitting something
   */
  private handleHit(): void {
    if (this.hasHit) return;
    
    this.hasHit = true;
    
    // Stick the arrow in the ground
    this.velocity.set(0, 0, 0);
    
    // Rotate arrow to stick in the ground
    const groundNormal = new THREE.Vector3(0, 1, 0);
    const hitAngle = this.direction.angleTo(groundNormal);
    
    // Create a rotation axis perpendicular to both vectors
    const rotationAxis = new THREE.Vector3().crossVectors(this.direction, groundNormal).normalize();
    
    // Apply rotation
    const quaternion = new THREE.Quaternion().setFromAxisAngle(rotationAxis, hitAngle);
    this.object.quaternion.premultiply(quaternion);
    
    // Reduce lifetime for stuck arrows
    this.lifetime = Math.min(this.lifetime, this.currentLifetime + 2.0);
  }
  
  /**
   * Deactivates the arrow
   */
  public deactivate(): void {
    if (!this.active) return;
    
    this.active = false;
    this.scene.remove(this.object);
  }
  
  /**
   * Gets the arrow's position
   * @returns The position
   */
  public getPosition(): THREE.Vector3 {
    return this.position.clone();
  }
  
  /**
   * Gets the arrow's damage
   * @returns The damage
   */
  public getDamage(): number {
    return this.damage;
  }
  
  /**
   * Checks if the arrow is active
   * @returns True if active
   */
  public isActive(): boolean {
    return this.active;
  }
  
  /**
   * Checks if the arrow has hit something
   * @returns True if the arrow has hit something
   */
  public hasHitSomething(): boolean {
    return this.hasHit;
  }
  
  /**
   * Disposes of resources
   */
  public dispose(): void {
    this.deactivate();
    
    if (this.arrowBody.geometry) {
      this.arrowBody.geometry.dispose();
    }
    
    if (this.arrowBody.material instanceof THREE.Material) {
      this.arrowBody.material.dispose();
    }
    
    if (this.arrowHead.geometry) {
      this.arrowHead.geometry.dispose();
    }
    
    if (this.arrowHead.material instanceof THREE.Material) {
      this.arrowHead.material.dispose();
    }
  }
} 